/**
 * EPC Edit Page
 * Page for editing existing EPC using multi-step wizard
 *
 * @version 2025.2.0
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Alert,
  CircularProgress,
} from '@mui/material';
import { PageShell } from '../../components';
import { AvisoDeFalhaAoCarregar } from '../../components/AvisoDeFalhaAoCarregar';
import { falhaDeCarga, textoDaFalhaDeCarga, type FalhaDeCarga } from '../../utils/acessoNegado';
import { epcService } from '../../services/epcService';
import { Epc, EpcFormData } from '../../types/epc';
import { EPCFormWizard } from './components';

export const EPCEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [epc, setEpc] = useState<Epc | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [falha, setFalha] = useState<FalhaDeCarga | null>(null);

  useEffect(() => {
    const loadEpc = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await epcService.getEpcById(id);
        setEpc(data);
      } catch (err: unknown) {
        setFalha(
          falhaDeCarga(err, 'Não foi possível carregar o EPC. Tente de novo.'),
        );
      } finally {
        setLoading(false);
      }
    };

    loadEpc();
  }, [id]);

  const handleSubmit = async (data: EpcFormData) => {
    if (!id) return;

    try {
      setIsSubmitting(true);
      setError(null);
      await epcService.updateEpc(id, data);
      navigate('/epcs');
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message ?? 'Erro ao atualizar EPC');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/epcs');
  };

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'EPCs', path: '/epcs' },
    { label: 'Editar EPC' },
  ];

  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar EPC">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!epc) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar EPC">
        <AvisoDeFalhaAoCarregar
          mensagem={textoDaFalhaDeCarga(falha, 'EPC não encontrado.')}
          rotuloDoBotao="Voltar para EPCs"
          aoVoltar={() => navigate('/epcs')}
        />
      </PageShell>
    );
  }

  return (
    <PageShell breadcrumbs={breadcrumbs} title="Editar EPC" subtitle={epc.nome}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardHeader title="Dados do EPC" />
        <CardContent>
          <EPCFormWizard
            initialData={epc}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default EPCEditPage;
